const { createElement } = window.BAO.Utils

class BaoProductMedia extends window.BAO.Carousel.Element {
  #thumbnailsPerView = 10
  #thumbnailResizeObserver = null

  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  renderTemplate () {
    const template = createElement('div', {
      class: 'prd-MediaZoom',
      'data-bao-product-media-external-el': 'wrapper',
    })
    template.insertAdjacentHTML(
      'afterbegin',
      `
      ${this.renderThumbnails()}

      ${this.renderImages()}

      <button type="button" class="prd-MediaZoom_Close">
        <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 14 14" width="14" height="14"><path d="M.693 1.4l11.749 11.749.707-.707L1.4.693z"></path><path d="M12.442.693L.693 12.442l.707.707L13.149 1.4z"></path></svg>
        <span class="util-ScreenReaderOnly">Close</span>
      </button>
    `,
    )

    Array.from(template.querySelectorAll('[data-thumbnail]')).forEach(
      thumbnail => {
        thumbnail.addEventListener('click', () => {
          template
            .querySelector(
              `[data-slide="${thumbnail.getAttribute('data-thumbnail')}"]`,
            )
            .scrollIntoView({ behavior: 'smooth' })
          template.querySelectorAll('[data-thumbnail]').forEach(
            tn => {
              tn.setAttribute('aria-current', false)
            },
          )
          thumbnail.setAttribute('aria-current', true)
        })
      },
    )

    return template
  }

  renderThumbnails () {
    const template = createElement('div', {
      class: 'prd-MediaZoom_Thumbnails',
    })

    template.appendChild(this.els.thumbnails.element.cloneNode(true))

    return template.outerHTML
  }

  renderImages () {
    const template = createElement('div', {
      class: 'prd-MediaZoom_Slides',
    })
    template.appendChild(this.els.slides.element.cloneNode(true))

    return template.outerHTML
  }

  static get requiredElements () {
    return super.requiredElements.concat([
      'slides',
      'slide',
      'dot',
      'thumbnail',
      'previous',
      'next',
      'thumbnail-previous',
      'thumbnail-next',
    ])
  }

  setupListeners () {
    super.setupListeners()

    this.els.slide.elements.forEach(element => {
      this.listeners.add(element, 'click', this.onImageClick.bind(this))
    })

    this.els.thumbnail.elements.forEach(element => {
      this.listeners.add(element, 'click', this.trackThumbnailClick.bind(this))
    })

    window.BAO.eventBus.addEventListener(window.BAO.EVENTS.BUNDLE_COMPONENT.OPTION_SELECTED, this.handleBundleComponentOptionSelected.bind(this))
  }

  afterInitialiseCarousel () {
    this.syncThumbnailColumnHeight()
    this.initialiseThumbnailCarousel()
    this.syncThumbnailViewportFromMainCarousel(this.carousel)
    this.observeThumbnailColumnHeight()
  }

  observeThumbnailColumnHeight () {
    if (this.#thumbnailResizeObserver || !this.els.slides?.element) return

    this.#thumbnailResizeObserver = new ResizeObserver(() => {
      this.syncThumbnailColumnHeight()
      this.updateThumbnailCarouselPerView()
    })
    this.#thumbnailResizeObserver.observe(this.els.slides.element)
  }

  syncThumbnailColumnHeight () {
    if (!window.matchMedia('(min-width: 990px)').matches) return

    const paginationEl = this.els['thumbnail-previous']?.element?.closest('.prd-Media_ThumbnailPagination')
    const carouselHeight = this.els.slides?.element?.offsetHeight

    if (!paginationEl || !carouselHeight) return

    paginationEl.style.setProperty('--prd-media-carousel-height', `${carouselHeight}px`)
  }

  isThumbnailArrowVisible (arrowEl) {
    return Boolean(arrowEl) && !arrowEl.hidden && !arrowEl.disabled
  }

  getThumbnailSlidesPerView () {
    const paginationEl = this.els['thumbnail-previous']?.element?.closest('.prd-Media_ThumbnailPagination')
    const previousEl = this.els['thumbnail-previous']?.element
    const nextEl = this.els['thumbnail-next']?.element
    const arrowEls = [previousEl, nextEl].filter(Boolean)
    const visibleArrowCount = arrowEls.filter(arrowEl =>
      this.isThumbnailArrowVisible(arrowEl),
    ).length
    const flexItemCount = visibleArrowCount + 1
    const gapTotal = Math.max(0, flexItemCount - 1) * 10
    const arrowHeight = arrowEls.reduce((total, arrowEl) => {
      return total + (this.isThumbnailArrowVisible(arrowEl) ? arrowEl.offsetHeight : 0)
    }, 0)
    const listHeight = (paginationEl?.offsetHeight ?? 0) - arrowHeight - gapTotal
    const firstThumbnail = this.els.thumbnail.elements[0]

    if (!firstThumbnail || listHeight <= 0) return 1

    let thumbnailHeight = firstThumbnail.getBoundingClientRect().height

    if (!thumbnailHeight || !this.thumbnailCarousel) {
      const thumbnailWidth = firstThumbnail.offsetWidth || 54
      const img = firstThumbnail.querySelector('img')

      if (img?.naturalWidth) {
        thumbnailHeight = thumbnailWidth * (img.naturalHeight / img.naturalWidth)
      } else {
        thumbnailHeight = thumbnailWidth * 1.25
      }
    }

    if (!thumbnailHeight) return 1

    return Math.max(
      1,
      Math.min(
        this.#thumbnailsPerView,
        Math.floor((listHeight + 10) / (thumbnailHeight + 10)),
      ),
    )
  }

  updateThumbnailCarouselPerView () {
    if (!this.thumbnailCarousel) return

    const perView = this.getThumbnailSlidesPerView()

    if (this.thumbnailCarousel.options.slides.perView === perView) return

    this.thumbnailCarousel.update({
      ...this.thumbnailCarousel.options,
      slides: {
        ...this.thumbnailCarousel.options.slides,
        perView,
      },
    })
  }

  initialiseThumbnailCarousel () {
    const thumbnails = this.els.thumbnail.elements
    const previousEl = this.els['thumbnail-previous'].element
    const nextEl = this.els['thumbnail-next'].element

    if (thumbnails.length <= this.#thumbnailsPerView) {
      previousEl.hidden = true
      nextEl.hidden = true

      return
    }

    previousEl.hidden = false
    nextEl.hidden = false

    this.els.thumbnails.element.classList.add('keen-slider')
    thumbnails.forEach(thumbnail => {
      thumbnail.classList.add('keen-slider__slide')
    })

    this.syncThumbnailColumnHeight()

    const perView = this.getThumbnailSlidesPerView()

    this.thumbnailCarousel = new window.KeenSlider(
      this.els.thumbnails.element,
      {
        selector: this.els.thumbnail.selector,
        vertical: true,
        loop: false,
        defaultAnimation: {
          duration: 300,
        },
        slides: {
          perView,
          spacing: 10,
        },
        created: () => {
          requestAnimationFrame(() => {
            this.updateThumbnailCarouselPerView()
          })
        },
      },
      [
        window.BAO.Carousel.useArrows({
          previous: previousEl,
          next: nextEl,
        }),
        carousel => {
          const syncDisabledArrowVisibility = () => {
            const { abs, maxIdx, min } = carousel.track.details

            previousEl.disabled = abs === min
            nextEl.disabled = abs === maxIdx
            previousEl.hidden = previousEl.disabled
            nextEl.hidden = nextEl.disabled
            this.updateThumbnailCarouselPerView()
          }

          carousel.on('created', syncDisabledArrowVisibility)
          carousel.on('slideChanged', syncDisabledArrowVisibility)
          carousel.on('updated', syncDisabledArrowVisibility)
        },
      ],
    )
  }

  syncThumbnailViewportFromMainCarousel (slider) {
    if (!slider?.track?.details) return

    this.ensureActiveThumbnailVisible(slider.track.details.abs)
  }

  ensureActiveThumbnailVisible (activeIndex) {
    if (!this.thumbnailCarousel) return

    const perView = this.thumbnailCarousel.options.slides.perView
    const { rel, maxIdx } = this.thumbnailCarousel.track.details
    const visibleEnd = rel + perView - 1

    if (activeIndex < rel) {
      this.thumbnailCarousel.moveToIdx(activeIndex)

      return
    }

    if (activeIndex > visibleEnd) {
      this.thumbnailCarousel.moveToIdx(
        Math.min(activeIndex - perView + 1, maxIdx),
      )
    }
  }

  handleBundleComponentOptionSelected (event) {
    const slideIndex = this.carousel?.slides?.find(el => el.dataset?.galleryImageHandle?.includes(event.detail?.value))?.dataset?.slide

    if (!slideIndex && slideIndex !== 0) return

    this.carousel.moveToIdx(Number(slideIndex))
    this.bringGalleryIntoView()
  }

  bringGalleryIntoView () {
    if (this.closest('quick-add-modal')) return
    if (window.innerWidth > 989) return

    const mediaEl = this.closest('[data-bao-product-media-el="root"]') || this

    const headerEl = document.getElementById('shopify-section-header')
    const headerOffset = headerEl?.getBoundingClientRect().height ?? 0
    const scrollingElement = document.scrollingElement || document.documentElement
    const top = Math.max(
      mediaEl.getBoundingClientRect().top + scrollingElement.scrollTop - headerOffset,
      0,
    )

    scrollingElement.scrollTop = top
    mediaEl.style.scrollMarginTop = `${headerOffset}px`
    mediaEl.scrollIntoView({ behavior: "smooth", block: 'start', inline: 'nearest' })
  }

  onImageClick (event) {
    const image = event.target.closest(this.els.slide.selector)

    if (!image) return

    let slideIndex = image.dataset.index

    if (slideIndex === undefined) {
      slideIndex = event.target.closest('.prd-Media_Slide').getAttribute('data-slide')
    }

    this.showLightbox(slideIndex)

    window.dispatchEvent(new CustomEvent('PDP-gallery-click', {
      detail: {
        mediaIndex: slideIndex,
      },
    }))
  }

  trackThumbnailClick (event) {
    const thumbnail = event.target.closest(this.els.thumbnail.selector)
    if (!thumbnail) return

    const thumbnailIndex = thumbnail.dataset?.thumbnail

    window.dispatchEvent(new CustomEvent('PDP-gallery-thumbnail-click', {
      detail: {
        mediaIndex: thumbnailIndex,
      },
    }))
  }

  showLightbox (index) {
    const lightboxNode = this.renderTemplate()
    document.documentElement.appendChild(lightboxNode)

    const lightboxController = new AbortController()
    const signal = lightboxController.signal

    const closeLightbox = () => {
      lightboxNode.parentNode.removeChild(lightboxNode)
      lightboxController.abort()
      document.querySelector('html').classList.remove('modal-open')
    }

    window.addEventListener(
      'keydown',
      ev => {
        if (ev.key === 'Escape') {
          closeLightbox()
        }
      },
      {
        signal,
      },
    )

    lightboxNode
      .querySelector('.prd-MediaZoom_Close')
      .addEventListener('click', () => {
        closeLightbox()
      })

    lightboxNode.addEventListener('click', ev => {
      if (ev.target.closest('.prd-Media_Thumbnails')) return

      closeLightbox()
    })
    document.querySelectorAll('.prd-MediaZoom_Thumbnails [data-thumbnail]').forEach(
      tn => {
        tn.setAttribute('aria-current', false)
      },
    )
    document.querySelector('html').classList.add('modal-open')

    if (index) {
      document.querySelector(`.prd-MediaZoom .prd-Media_Thumbnail[data-thumbnail="${index}"]`).click()
    }
  }

  initialiseCarousel () {
    if (this.els.slide.elements.length > 3) {
      return new window.KeenSlider(
        this.els.slides.element,
        {
          selector: this.els.slide.selector,
          defaultAnimation: {
            duration: 500,
          },
          loop: true,
          mode: 'snap',
          slides: {
            perView: 1,
            spacing: 0,
          },
          initial: 1,
          slideChanged: slider => {
            this.syncThumbnailViewportFromMainCarousel(slider)
          },
          breakpoints: {
            '(min-width: 990px)': {
              initial: this.els.slide.elements.length - 1,
              vertical: true,
            },
          },
        },
        [
          window.BAO.Carousel.useInitialised(),
          window.BAO.Carousel.useDots(
            this.els.dot.elements,
            (carousel, dot, index) => {
              const amountOfSlides = carousel.slides.length
              const slidesPerView = carousel.options.slides.perView
              const shouldHideSlide =
                index >= amountOfSlides - (slidesPerView - 1)
              dot.setAttribute('aria-hidden', shouldHideSlide.toString())
            },
          ),
          window.BAO.Carousel.useThumbnails(this.els.thumbnail.elements),
          window.BAO.Carousel.useArrows({
            previous: this.els.previous.element,
            next: this.els.next.element,
          }),
        ],
      )
    } else {
      return new window.KeenSlider(
        this.els.slides.element,
        {
          selector: this.els.slide.selector,
          defaultAnimation: {
            duration: 500,
          },
          loop: true,
          mode: 'snap',
          slides: {
            perView: 1,
            spacing: 0,
          },
          slideChanged: slider => {
            this.syncThumbnailViewportFromMainCarousel(slider)
          },
        },
        [
          window.BAO.Carousel.useInitialised(),
          window.BAO.Carousel.useDots(
            this.els.dot.elements,
            (carousel, dot, index) => {
              const amountOfSlides = carousel.slides.length
              const slidesPerView = carousel.options.slides.perView
              const shouldHideSlide =
                index >= amountOfSlides - (slidesPerView - 1)
              dot.setAttribute('aria-hidden', shouldHideSlide.toString())
            },
          ),
          window.BAO.Carousel.useThumbnails(this.els.thumbnail.elements),
          window.BAO.Carousel.useArrows({
            previous: this.els.previous.element,
            next: this.els.next.element,
          }),
        ],
      )
    }
  }
}

if (!customElements.get('bao-product-media')) {
  customElements.define('bao-product-media', BaoProductMedia)
}
